import { useState } from "react";
import { XIcon } from "@phosphor-icons/react";

import type { RequestStatusT } from "../domain/types/requestStatus.types";

import { VACATION_REQUESTS_STRINGS } from "../constants/strings";

import { useCurrentCompany } from "hooks/useCurrentCompany";
import { useRequestDetail, useRequestFiles } from "../hooks";

import { ColaboraButton, Heading, SidePortal } from "components/v2";

import { useQueryError } from "hooks/useQueryError";
import { Toast } from "components/toast";

import { canUpdateStatus } from "../utils/requestStatus";

import CollaboratorCard from "./CollaboratorCard";
import OverlappingSection from "./OverlappingSection";
import RequestBasicInfo from "./RequestBasicInfo";
import RequestData from "./RequestData";
import FilesSection from "./FilesSection";
import RejectRequest from "./RejectRequest";

import { REQUEST_STATUS } from "../constants/status";

const RequestDetailSkeleton = () => (
  <>
    {/* CollaboratorCard */}
    <div className="flex gap-3">
      <div className="w-16 h-16 rounded-full bg-gray-200 animate-pulse shrink-0" />
      <div className="flex flex-col gap-1.5 justify-center flex-1">
        <div className="h-2 w-36 bg-gray-200 rounded animate-pulse" />
        <div className="h-2 w-28 bg-gray-200 rounded animate-pulse" />
        <div className="h-2 w-20 bg-gray-200 rounded animate-pulse" />
      </div>
    </div>

    {/* RequestBasicInfo */}
    <div className="flex flex-col bg-gray-100 rounded-lg p-3 gap-3 animate-pulse">
      <div className="flex gap-3">
        <div className="w-12 h-12 bg-gray-200 rounded-lg shrink-0" />
        <div className="flex flex-col gap-1.5 flex-1">
          <div className="h-2 w-32 bg-gray-200 rounded" />
          <div className="h-2 w-20 bg-gray-200 rounded" />
        </div>
        <div className="h-2 w-20 bg-gray-200 rounded-full" />
      </div>
      <div className="flex justify-between">
        <div className="h-3 w-20 bg-gray-200 rounded" />
        <div className="h-3 w-20 bg-gray-200 rounded" />
      </div>
    </div>

    {/* RequestData */}
    <div className="flex flex-col gap-3 animate-pulse">
      <div className="flex justify-between">
        <div className="h-2 w-24 bg-gray-200 rounded" />
        <div className="h-2 w-28 bg-gray-200 rounded" />
      </div>
      <div className="flex justify-between">
        <div className="h-2 w-28 bg-gray-200 rounded" />
        <div className="h-2 w-20 bg-gray-200 rounded" />
      </div>
      <div className="flex flex-col gap-1.5">
        <div className="h-2 w-24 bg-gray-200 rounded" />
        <div className="h-2 w-full bg-gray-200 rounded" />
      </div>
    </div>

    {/* OverlappingSection */}
    <div className="flex flex-col gap-2 animate-pulse">
      <div className="h-2 w-32 bg-gray-200 rounded" />
      {[0, 1].map((i) => (
        <div key={i} className="flex bg-gray-100 rounded-lg p-2 gap-2">
          <div className="w-7 h-7 rounded-full bg-gray-200 shrink-0" />
          <div className="flex flex-col gap-1.5 flex-1">
            <div className="h-2 w-28 bg-gray-200 rounded" />
            <div className="h-2 w-36 bg-gray-200 rounded" />
            <div className="h-2 w-20 bg-gray-200 rounded-lg" />
          </div>
          <div className="h-2 w-16 bg-gray-200 rounded" />
        </div>
      ))}
    </div>
  </>
);

type RequestDetailPropsT = {
  requestId: string;
  onClose: () => void;
};

const RequestDetail = ({ requestId, onClose }: RequestDetailPropsT) => {
  const companyInfo = useCurrentCompany();
  const [isRejectOpen, setIsRejectOpen] = useState(false);

  const { data, isLoading, isError, error, approveRequest, applyRequest } =
    useRequestDetail({
      requestId,
      colaboraInstanceId: companyInfo?.instanceId,
    });

  const {
    data: dataFiles,
    isLoading: isLoadingFiles,
    isError: isErrorFiles,
    error: errorFiles,
  } = useRequestFiles({
    requestId,
    colaboraInstanceId: companyInfo?.instanceId,
  });

  const handleApprove = async () => {
    try {
      const { failed } = await approveRequest.mutateAsync({
        requestsToUpdate: [{ requestId, newStatus: REQUEST_STATUS.APPROVED }],
      });

      if (failed.length > 0) {
        Toast("warning", VACATION_REQUESTS_STRINGS.UPDATE_DATA_ERROR_TITLE);
        approveRequest.reset();
      }
    } catch (error) {
      Toast(
        "error",
        VACATION_REQUESTS_STRINGS.UPDATE_DATA_ERROR_TITLE,
        error.message,
      );
    }
  };

  const handleApply = async () => {
    try {
      const { results } = await applyRequest.mutateAsync({
        requestIds: [requestId],
      });

      const result = results?.[0];
      if (!result?.isSuccess) {
        applyRequest.reset();
        Toast(
          "error",
          VACATION_REQUESTS_STRINGS.APPLY_ERROR_TITLE,
          result?.status,
        );
      }
    } catch (error) {
      Toast(
        "error",
        VACATION_REQUESTS_STRINGS.APPLY_ERROR_TITLE,
        error.message,
      );
    }
  };

  const handleOpenReject = () => setIsRejectOpen(true);
  const handleRejectClose = () => {
    setIsRejectOpen(false);
    onClose();
  };

  useQueryError({
    isError,
    error,
    message: VACATION_REQUESTS_STRINGS.DETAIL_ERROR_MESSAGE,
  });

  useQueryError({
    isError: isErrorFiles,
    error: errorFiles,
    message: VACATION_REQUESTS_STRINGS.FILES_ERROR_MESSAGE,
  });

  if (!isLoading && !data) return null;

  const {
    collaborator,
    type,
    totalDays,
    status,
    startDate,
    endDate,
    processedBy,
    createdDate,
    reason,
    audit,
    leader,
    balance,
    overlapping,
  } = data ?? {};

  return (
    <div className="relative flex flex-col h-full">
      <div className="flex items-center justify-between px-4 py-3 shrink-0">
        <Heading level={2}>{VACATION_REQUESTS_STRINGS.DETAIL_TITLE}</Heading>
        <button
          className="cursor-pointer p-2 shrink-0"
          onClick={onClose}
          type="button"
        >
          <XIcon size={20} />
        </button>
      </div>

      <div
        className={`flex-1 overflow-y-auto flex flex-col gap-4 px-4 ${isLoading ? "pb-32" : "pb-4"}`}
      >
        {isLoading || isLoadingFiles ? (
          <RequestDetailSkeleton />
        ) : (
          <>
            <CollaboratorCard collaborator={collaborator} />

            <RequestBasicInfo
              type={type}
              totalDays={totalDays}
              status={status}
              startDate={startDate}
              endDate={endDate}
            />

            <RequestData
              processedBy={processedBy}
              status={status}
              createdDate={createdDate}
              reason={reason}
              balance={balance}
              history={audit?.history}
            />

            <FilesSection files={dataFiles?.files} />

            {!!leader && (
              <OverlappingSection overlappingAbsences={overlapping} />
            )}
          </>
        )}
      </div>

      {!isLoading &&
        !isLoadingFiles &&
        canUpdateStatus(status as RequestStatusT) && (
          <div className="flex flex-col gap-2 p-4 border-t">
            <ColaboraButton
              label={VACATION_REQUESTS_STRINGS.TEXT_TOOLTIP_REJECT}
              errorLabel={VACATION_REQUESTS_STRINGS.DETAIL_REJECT_ERROR_LABEL}
              successLabel={
                VACATION_REQUESTS_STRINGS.DETAIL_REJECT_SUCCESS_LABEL
              }
              variant="secondary"
              onClick={handleOpenReject}
            />
            <ColaboraButton
              label={
                status === REQUEST_STATUS.PENDING
                  ? VACATION_REQUESTS_STRINGS.TEXT_TOOLTIP_APPROVE
                  : VACATION_REQUESTS_STRINGS.TEXT_TOOLTIP_APPLY
              }
              successLabel={
                status === REQUEST_STATUS.PENDING
                  ? VACATION_REQUESTS_STRINGS.DETAIL_APPROVE_SUCCESS_LABEL
                  : VACATION_REQUESTS_STRINGS.DETAIL_APPLY_SUCCESS_LABEL
              }
              errorLabel={
                status === REQUEST_STATUS.PENDING
                  ? VACATION_REQUESTS_STRINGS.DETAIL_APPROVE_ERROR_LABEL
                  : VACATION_REQUESTS_STRINGS.DETAIL_APPLY_ERROR_LABEL
              }
              isLoading={
                status === REQUEST_STATUS.PENDING
                  ? approveRequest.isPending
                  : applyRequest.isPending
              }
              isSuccess={
                status === REQUEST_STATUS.PENDING
                  ? approveRequest.isSuccess
                  : applyRequest.isSuccess
              }
              isError={
                status === REQUEST_STATUS.PENDING
                  ? approveRequest.isError
                  : applyRequest.isError
              }
              onClick={
                status === REQUEST_STATUS.PENDING ? handleApprove : handleApply
              }
              onSuccessAnimationEnd={onClose}
            />
          </div>
        )}

      <SidePortal
        showContent={isRejectOpen}
        onClose={() => setIsRejectOpen(false)}
      >
        {isRejectOpen && (
          <RejectRequest requestIds={[requestId]} onClose={handleRejectClose} />
        )}
      </SidePortal>
    </div>
  );
};

export default RequestDetail;
